import { Injectable, signal, WritableSignal } from '@angular/core';
import branch, { BranchError, SessionData } from 'branch-sdk';
import { AuthActions } from '@features/auth/store';
import { Store } from '@ngrx/store';
import { SnackbarApiService } from './snackbar-api.service';
import { environment } from '@environment';
import * as Sentry from '@sentry/angular';
import {
  BranchDataParsed,
  getBranchLinkSource,
  isBranchPartnerSignUpDeeplink,
  isBranchPartnerSignupCookieSource,
} from '@core/models/branch-deeplink.models';
import {
  clearBranchSignupSourceCookie,
  readBranchSignupSourceCookie,
  writeBranchSignupSourceCookie,
} from '@core/utils/branch-signup-source-cookie.util';

@Injectable({
  providedIn: 'root',
})
export class BranchService {
  branchData: WritableSignal<SessionData | null> = signal(null);
  token: WritableSignal<string | null> = signal(null);
  path: WritableSignal<string | null> = signal(null);

  /** `source` from Branch partner deeplink, passed on user sign-up. */
  partnerReferralSource: WritableSignal<string | null> = signal(null);

  private initPromise: Promise<void> | null = null;

  constructor(
    private store: Store,
    private snackbarApiService: SnackbarApiService
  ) {
    const cookieSource = readBranchSignupSourceCookie();
    if (cookieSource) {
      this.partnerReferralSource.set(cookieSource);
    }
  }

  initializeBranch(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise(resolve => {
      branch.init(environment.branchKey, {}, (err: BranchError, data: SessionData | null) => {
        try {
          if (data?.data_parsed) {
            const deepLinkData = data.data_parsed as BranchDataParsed;
            this.branchData.set(data);
            const path = typeof deepLinkData.$deeplink_path === 'string' ? deepLinkData.$deeplink_path : '';
            this.path.set(path || null);

            const linkSource = getBranchLinkSource(deepLinkData);
            const partnerDeeplink = isBranchPartnerSignUpDeeplink(path);
            const cookieEligible = isBranchPartnerSignupCookieSource(linkSource);

            if (partnerDeeplink) {
              if (linkSource) {
                this.partnerReferralSource.set(linkSource);
              }
              if (cookieEligible && linkSource) {
                writeBranchSignupSourceCookie(linkSource);
              }
            }

            if (path === 'reset-password' || path === 'instructor-registration') {
              const token = deepLinkData.$custom_meta_tags?.token;
              Sentry.addBreadcrumb({
                category: 'branch',
                message: `Branch init: deeplink path "${path}" detected`,
                data: { path, hasToken: !!token },
                level: 'info',
              });
              if (token) {
                this.store.dispatch(AuthActions.setToken({ token }));
                this.token.set(token);
              } else {
                Sentry.captureMessage(`Branch deeplink "${path}" received without token`, {
                  level: 'warning',
                  extra: { deepLinkData },
                });
              }
            }
          } else {
            Sentry.addBreadcrumb({
              category: 'branch',
              message: 'Branch init completed with no data_parsed',
              level: 'info',
            });
          }
          if (err) {
            console.log(err);
            Sentry.captureMessage('Branch init error', {
              level: 'error',
              extra: { error: err },
            });
            this.snackbarApiService.success('branch failed');
          }
        } finally {
          resolve();
        }
      });
    });

    return this.initPromise;
  }

  clearData(): void {
    this.branchData.set(null);
    this.token.set(null);
    this.path.set(null);
    this.partnerReferralSource.set(null);
    this.initPromise = null;
  }

  clearPartnerReferralSource(): void {
    this.partnerReferralSource.set(null);
    clearBranchSignupSourceCookie();
  }
}
